<template>
  <div @click="selectedItem = null">
    <breadcrumbs home="Batch product move"/>

    <v-card flat>
      <v-form v-model="valid" ref="form">
        <v-container class="pt-6 px-6 pb-0 purtrak-scrollable" fluid>
          <v-row align="center">
            <v-col cols="6" sm="4" lg="3">
              <date-picker v-model="transaction.date" @change="refresh()" hide-details/>
            </v-col>
            <v-col cols="6" sm="2">
              <v-switch label="Bottles only" dense hide-details class="mt-0" v-model="transaction.bottlesOnly" @click="convertToBottles()"/>
            </v-col>
            <v-col cols="6" sm="4" lg="2">
              <v-text-field label="Info" v-model="transaction.info" :rules="[fieldRules.maxLength(20)]" counter="255" outlined dense hide-details/>
            </v-col>
            <v-col cols="6" sm="4" lg="2">
              <v-autocomplete label="Status" dense outlined hide-details :items="[{value:'Request',text:'Request'},{value:'Pending',text:'Pending'},{value:'InTransit',text:'In transit'},{value:'Confirmed',text:'Confirmed'}]" v-model="transaction.status"/>
            </v-col>
            <v-col cols="6" sm="4" lg="2" offset="1">
              <v-text-field label="#" v-model="transaction.moveNumber" :rules="[fieldRules.required,fieldRules.maxLength(20)]" outlined hide-details dense :counter="20"/>
            </v-col>
            <v-col cols="1" v-if="$vuetify.breakpoint.mobile">
              <scan-code @code="scan" :products="allProducts"/>
            </v-col>
          </v-row>

          <v-row v-if="transaction.defaultFromSiteId && transaction.defaultToSiteId">
            <v-col cols="6" sm="4" lg="2">
              <v-combobox label="Ship via" v-model="transaction.shipVia" outlined dense :rules="[fieldRules.maxLength(20)]" hide-details :items="shipVias"/>
            </v-col>
            <v-col cols="6" sm="4" lg="1">
              <decimal-number-input label="Pallets" v-model="transaction.pallets" prefix="" :precision="0" hide-details/>
            </v-col>
            <v-col cols="6" sm="4" lg="2">
              <v-text-field label="Ref #" v-model="transaction.number" :rules="[fieldRules.maxLength(20)]" outlined dense hide-details/>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <outlined-block :rules="[() => products.length>0 || 'Products required']">
                <v-btn v-if="$vuetify.breakpoint.mobile" outlined color="primary" @click="addProduct()" small class="mt-4 ml-4">Add product</v-btn>
                <v-data-table width="100%"
                              calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer fixed-header
                              :items="products" item-key="key" no-data-text="No product moves specified"
                              :headers="headers" class="no-sort mt-1"
                              :item-class="item => item.key=== selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}">
                  <template v-slot:header.action>
                    <scan-code @code="scan" :products="allProducts"/>
                  </template>
                  <template v-slot:header.fromInBond="{header}">
                    From in-bond
                    <v-simple-checkbox dense :value="transaction.defaultFromInBond!==0" :indeterminate="transaction.defaultFromInBond===2" @click="clickDefaultFromInBond"/>
                  </template>
                  <template v-slot:header.fromSite="{header}">
                    From site
                    <v-autocomplete dense v-model="transaction.defaultFromSiteId" :items="transaction.defaultFromInBond===1?defaultExciseSites:defaultSites" item-text="name" item-value="id" hide-details="" class="text-caption"
                                    @change="changeDefaultFromSiteId"/>
                  </template>
                  <template v-slot:header.toSite="{header}">
                    To site
                    <v-autocomplete dense v-model="transaction.defaultToSiteId" :items="transaction.defaultToDutyPaid!==0?defaultSites:defaultExciseSites" item-text="name" item-value="id" hide-details="" class="text-caption"
                                    @change="changeDefaultToSiteId"/>
                  </template>
                  <template v-slot:header.toDutyPaid="{header}">
                    To duty-paid
                    <v-simple-checkbox dense :value="transaction.defaultToDutyPaid!==0" :indeterminate="transaction.defaultToDutyPaid===2" @click="clickDefaultToDutyPaid" :disabled="transaction.defaultFromInBond===0"/>
                  </template>

                  <template v-slot:item.action="{item,index}">
                    <v-btn icon @click="products.splice(index, 1)"><v-icon small>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.product="{item}">
                    <template v-if="selectedItem === item.key">
                      <multi-level-select v-if="!!categories" :menuData="categories" :second.sync="item.productId" outlined/>
                      <v-autocomplete v-else v-model="item.productId" dense :items="allProducts" item-text="name" item-value="id" :rules="[fieldRules.required]" class="text-caption" outlined/>
                    </template>
                    <div v-else :class="{'error--text':!item.productId}">{{productName(item)}}</div>
                  </template>
                  <template v-slot:item.fromInBond="{item}">
                    <v-simple-checkbox v-if="selectedItem === item.key" v-model="item.fromInBond" dense class="mt-n6" @click="check(item)" :disabled="transaction.defaultFromInBond!==2"/>
                    <v-icon v-else-if="item.fromInBond">mdi-check</v-icon>
                  </template>
                  <template v-slot:item.lot="{item}">
                    <template v-if="lotTracking(item)">
                      <v-combobox v-if="selectedItem === item.key" v-model="item.lot" dense :items="productLots(item.productId)" :rules="[fieldRules.required,fieldRules.maxLength(20)]" class="text-caption" outlined/>
                      <div v-else :class="{'error--text':item.lot==''}">{{item.lot || 'Required'}}</div>
                    </template>
                  </template>
                  <template v-slot:item.fromSite="{item}">
                    <v-autocomplete  v-if="selectedItem === item.key" v-model="item.fromSiteId" dense :items="item.fromInBond?exciseSites:sites" item-text="name" item-value="id" :rules="[fieldRules.required]" class="text-caption" :disabled="transaction.defaultFromSiteId!==''" outlined/>
                    <div v-else :class="{'error--text':!item.fromSiteId}">{{item.fromSiteId ? sites.find(site => site.id === item.fromSiteId).name : 'Required'}}</div>
                  </template>
                  <template v-slot:item.toSite="{item}">
                    <v-autocomplete v-if="selectedItem === item.key" v-model="item.toSiteId" dense :items="item.toDutyPaid?sites:exciseSites" item-text="name" item-value="id" :rules="[fieldRules.required]" class="text-caption" :disabled="transaction.defaultToSiteId!==''" outlined/>
                    <div v-else :class="{'error--text':!item.toSiteId}">{{item.toSiteId ? sites.find(site => site.id === item.toSiteId).name : 'Required'}}</div>
                  </template>
                  <template v-slot:item.toDutyPaid="{item}">
                    <v-simple-checkbox v-if="selectedItem === item.key" v-model="item.toDutyPaid" dense class="mt-n6" :disabled="!item.fromInBond || transaction.defaultToDutyPaid!==2" @click="check(item)"/>
                    <v-icon v-else-if="item.toDutyPaid">mdi-check</v-icon>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <template v-if="selectedItem === item.key">
                      <decimal-number-input v-if="!transaction.bottlesOnly" v-model="item.cases" prefix="" suffix="cases" :precision="0" class="text-caption"/>
                      <decimal-number-input v-model="item.bottles" prefix="" suffix="bottles" :precision="0" class="text-caption"/>
                    </template>
                    <template v-else>
                      <div v-if="!transaction.bottlesOnly" :class="{'error--text':item.cases < 0}">
                        {{item.cases | number(0)}} cases
                      </div>
                      <div :class="{'error--text':item.bottles < 0}">
                        {{item.bottles | number(0)}} bottles
                      </div>
                    </template>
                  </template>
                  <template v-slot:body.append="{headers}">
                    <tr class="no-hover" @click="selectedItem = null">
                      <td class="text-center">
                        <v-btn fab color="primary" x-small @click="addProduct()"><v-icon>mdi-plus</v-icon></v-btn>
                      </td>
                      <td :colspan="headers.length-1"/>
                    </tr>
                  </template>
               </v-data-table>
             </outlined-block>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" counter="255"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-textarea label="Special instructions" rows="2"
                  v-model="transaction.specialInstructions" outlined dense :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <v-spacer/>
        <v-btn plain color="primary" @click="cancel">Cancel</v-btn>
        <v-btn color="primary" outlined :loading="saving" @click="save(true)">Save as draft</v-btn>
        <v-btn color="primary" :loading="saving" @click="save(false)">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import createHierarchyList from '@/mixins/createHierarchyList'

export default {
  name: 'InventoryProductsBatchMoveEdit',
  mixins: [confirmLeave,createHierarchyList],
	props: {
    id: {type:String,required:false},
    copyId: {type:String,required:false}
	},
	data() {
		return {
      valid: true,
      transaction: {date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),moveNumber:'',comments:'',info:'',status:'Confirmed',bottlesOnly:false,
                    defaultFromInBond:2,defaultToDutyPaid:2,defaultFromSiteId:'',defaultToSiteId:'',
                    number:'',pallets:0,shipVia:'',specialInstructions:''},
      products: [],
      shipVias: [],
      saving:false,
      allProducts: [],
      categoriesList: [],
      categories: null,
      selectedItem: null
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(true);
    },
    exciseSites() {
      return this.$store.getters.sites(true, true);
    },
    defaultSites() {
      return [{name:'no default',id:''},{divider:true}, ...this.$store.getters.sites(true)];
    },
    defaultExciseSites() {
      return [{name:'no default',id:''},{divider:true}, ...this.$store.getters.sites(true, {B262:true})];
    },
    multipleSites() {
      return this.sites.length > 1;
    },
    headers() {
      const headers = [{text:'',value:'action',width:'4%',align:'center',cellClass:'pa-0',divider:true},
                      {text:'Product',value:'product',width:'24%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                      {text:'In-bond',value:'fromInBond',width:'5%',align:'center',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'}];
      if (this.company.settings.lotTrackingForProducts)
        headers.push({text:'Lot',value:'lot',width:'10%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'});
      if (this.multipleSites)
        headers.push({text:'From site',value:'fromSite',width:'20%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                     {text:'To site',value:'toSite',width:'20%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'});
      return [...headers, {text:'To duty-paid',value:'toDutyPaid',width:'5%',align:'center',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                          {text:'Quantity',value:'quantity',width:'12%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'}];
    }
  },
	async created() {
    this.$lazyListLoad(this.categoriesList, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Product'}]);
    const results = await this.$apiCall(`query InventoryProductsBatchMoveEdit($id:ID!, $edit:Boolean!) {
                                            productsBatchMove(id:$id) @include(if:$edit) {
                                              id,date,moveNumber,draft,info,status,comments,bottlesOnly,
                                              defaultFromInBond,defaultToDutyPaid,defaultFromSiteId,defaultToSiteId
                                              number,pallets,shipVia,specialInstructions
                                              lines {productId,fromInBond,lot,fromSiteId,toSiteId,toDutyPaid,bottles,cases}
                                            }
                                            shipVias {name}
                                            productsBatchMoveNextNumber
                                          }`,
                                        {edit:!!this.id || !!this.copyId, id:this.id ?? this.copyId ?? ''});
    if (!results.error) {
      this.shipVias = results.data.shipVias.map(shipVia => shipVia.name);
      if (this.id || this.copyId) {
        this.transaction = this.id ? results.data.productsBatchMove : {...results.data.productsBatchMove,id:null,date:this.transaction.date, info:'',status:'Confirmed'};
        let key = Date.now();
        this.transaction.lines.forEach(line => {
          line.key = key++;
          if (this.copyId)
            line.lot = '';
        });
        this.products = this.transaction.lines;
      }
      if (!this.id)
        this.transaction.moveNumber = results.data.productsBatchMoveNextNumber;
    }
    await this.refresh();
    this.$nextTick(() => this.$refs.form?.validate());
  },

	methods: {
    scan({type, id, quantity, cases, lot}) {
      if (type !== 'Product')
        return;
      lot = lot ?? '';
      let product = this.products.find(product => product.productId === id && product.lot === lot);
      if (!product) {
        product = this.addProduct();
        product.productId = id;
        product.lot = lot;
      }
      if (this.transaction.bottlesOnly && cases) {
        const productObject = this.allProducts.find(p => p.id === id);
        product.bottles += quantity * productObject.caseSize;
      }
      else
        product[cases?'cases':'bottles'] += quantity;
    },
    productName(product) {
      return this.allProducts.find(p => p.id === product.productId)?.name ?? 'Required';
    },
    lotTracking(product) {
      return product.fromInBond || this.company.settings.lotTrackingForDutyPaid;
    },
    clickDefaultFromInBond() {
      this.transaction.defaultFromInBond++;
      if (this.transaction.defaultFromInBond === 3)
        this.transaction.defaultFromInBond = 0;
      if (this.transaction.defaultFromInBond === 0)
        this.transaction.defaultToDutyPaid = 1;
      if (this.transaction.defaultFromInBond !== 2)
        for (const product of this.products) {
          product.fromInBond = this.transaction.defaultFromInBond === 1;
          this.check(product);
        }
      this.transaction.defaultFromSiteId = '';
    },
    clickDefaultToDutyPaid() {
      if (this.transaction.defaultFromInBond === 0)
        return;
      this.transaction.defaultToDutyPaid++;
      if (this.transaction.defaultToDutyPaid === 3)
        this.transaction.defaultToDutyPaid = 0;
      if (this.transaction.defaultToDutyPaid !== 2)
        for (const product of this.products) {
          product.toDutyPaid = this.transaction.defaultToDutyPaid === 1;
          this.check(product);
        }
      this.transaction.defaultToSiteId = '';
    },
    changeDefaultFromSiteId() {
      if (this.transaction.defaultFromSiteId)
        for (const product of this.products) {
          product.fromSiteId = this.transaction.defaultFromSiteId;
          this.check(product);
        }
    },
    changeDefaultToSiteId() {
      if (this.transaction.defaultToSiteId)
        for (const product of this.products) {
          product.toSiteId = this.transaction.defaultToSiteId;
          this.check(product);
        }
    },
    async refresh() {
      this.allProducts = [];
      await this.$lazyListLoad(this.allProducts, 'products2', 'id,name,caseSize,category{id},upc,scc,sites {siteId,inBondBottles,dutyPaidBottles,lot}', 100, [{field:'active', comparator:'eq', value:'true'}], [{name:'asof',value:this.transaction.date}]);

      if (this.categoriesList.length) {
        const categories = this.createHierarchyList(this.categoriesList);
        for (const category of categories) {
          const sub = this.allProducts.filter(product => product.category?.id === category.id);
          if (sub.length)
            category.menu = sub;
          else
            category.disabled = true;
        }
        this.categories = categories;
        const leftOver = this.allProducts.filter(product => !product.category?.id);
        if (leftOver.length)
          this.categories.push({divider:true},{name:'Other products',id:'x',menu:leftOver});
      }
    },
    addProduct() {
      const defaultSiteId = this.multipleSites ? '' : this.sites[0].id;
      const key = Date.now();
      const product = {productId:'', lot:'',
                        fromInBond: this.transaction.defaultFromInBond !== 2 ? this.transaction.defaultFromInBond===1 : true,
                        fromSiteId:this.transaction.defaultFromSiteId || defaultSiteId,
                        toSiteId:this.transaction.defaultToSiteId || defaultSiteId,
                        toDutyPaid:this.transaction.defaultToDutyPaid !== 2 ? this.transaction.defaultToDutyPaid===1 : true,
                        cases:0, bottles:0, key};
      this.products.push(product);
      this.selectedItem = key;
      this.$nextTick(() => this.$refs.form.validate());
      return product;
    },
    productLots(productId) {
      const sites = this.allProducts.find(p => p.id === productId)?.sites || [];
      return [...new Set(sites.map(site => site.lot))];
    },
    check(product) {
      if (!product.fromInBond)
        product.toDutyPaid = true;
      if (product.fromInBond && !this.exciseSites.some(s => s.id === product.fromSiteId)) {
        product.fromSiteId = '';
        this.$nextTick(() => this.$refs.form.validate());
      }
      if (!product.toDutyPaid && !this.sites.find(s => s.id === product.toSiteId)?.B262) {
        product.toSiteId = '';
        this.$nextTick(() => this.$refs.form.validate());
      }
    },
    cancel() {
      this.$router.back();
    },
    convertToBottles() {
      this.products.forEach(line => {
        if (line.productId) {
          const product = this.allProducts.find(product => product.id === line.productId);
          if (this.transaction.bottlesOnly) {
            line.bottles += line.cases * product.caseSize;
            line.cases = 0;
          }
          else {
            line.cases = Math.floor(line.bottles / product.caseSize);
            line.bottles -= line.cases * product.caseSize;
          }
        }
      });
    },
		async save(draft) {
      if (!this.valid ||
          this.products.some(product => !product.productId ||
                                        (this.company.settings.lotTrackingForProducts && this.lotTracking(product) && !product.lot) ||
                                        (this.multipleSites && (!product.fromSiteId || !product.toSiteId)) ||
                                        product.cases < 0 || product.bottles < 0)) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.saving = true;
      this.transaction.lines = this.products.map(product => {
        const {key, ...p} = product;
        return p;
      });
      this.transaction.draft = draft;
      const results = await this.$apiCall(`mutation InventoryProductsBatchMoveEdit($input: ProductBatchMoveInput!) {
                                              productsBatchMoveCrupdate(input:$input) {id}
                                            }`,
                                          {input:this.transaction});
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`Completed batch product move`});
      }
		}
	}
}
</script>